import { Component, OnInit, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { IonModal } from '@ionic/angular';
import { ActivatedRoute } from '@angular/router';
import { DataService } from 'src/app/data.service';
import { ColumnMode, DatatableComponent, SelectionType, SortType } from '@swimlane/ngx-datatable';

/** Egy tábla konfigurációs rekordja */
interface TorzsMeta {
  id: number;
  table_name: string;
  display_name: string;
  intezmeny_field: string | null;
  id_field: string;
  ordsz: number;
  menu_kategoria: string | null;
}

/** DESCRIBE eredmény egy sora */
interface ColumnMeta {
  Field: string;
  Type: string;
  Collation?: string | null;
  Null: string;
  Key: string;
  Default: string | null;
  Extra: string;
  Privileges?: string;
  Comment?: string;
}

@Component({
  selector: 'app-torzsadat',
  templateUrl: './torzsadat.component.html',
  styleUrls: ['./torzsadat.component.scss'],
})
export class TorzsadatComponent implements OnInit {

  protected readonly ColumnMode = ColumnMode;
  protected readonly SortType = SortType;
  protected readonly SelectionType = SelectionType;

  menuCategories = [
    { value: 'intezmeny', label: 'Intézmény' },
    { value: 'dolgozok', label: 'Dolgozók' },
    { value: 'tanulok', label: 'Tanulók' },
    { value: 'felulet', label: 'Felület' },
    { value: 'import', label: 'Import' },
    { value: 'besorolas', label: 'Besorolás' },
    { value: 'penzugy', label: 'Pénzügy' },
  ];

  /** Az URL-ből beolvasott szűrő kategória (null = nincs szűrés) */
  menuKategoria: string | null = null;

  /** Ha ?table=xxx param van: az oldalpanel rejtett, a tábla auto-kiválasztva */
  tableMode = false;

  get menuKategoriaLabel(): string {
    if (!this.menuKategoria) return '';
    return this.menuCategories.find(k => k.value === this.menuKategoria)?.label ?? this.menuKategoria;
  }

  // --- Tábla-konfiguráció ---
  tablaMeta: TorzsMeta[] = [];
  dbTablas: { table_name: string; comment: string }[] = [];
  activeMeta: TorzsMeta | null = null;
  metaForm: FormGroup;
  metaEditMode: 'add' | 'edit' = 'add';
  @ViewChild('metaModal') metaModal!: IonModal;

  // --- Adatok ---
  columns: ColumnMeta[] = [];
  rows: any[] = [];
  filteredRows: any[] = [];
  total = 0;
  page = 1;
  limit = 500;
  loading = false;

  // --- Paginator + keresés ---
  currentPageLimit = 10;
  currentVisible = 5;
  filterText = '';
  readonly pageLimitOptions = [
    { value: 10 }, { value: 25 }, { value: 50 }, { value: 100 }, { value: 250 },
  ];
  @ViewChild('table') table!: DatatableComponent;

  // --- Szerkesztő modal ---
  rowForm: FormGroup;
  rowEditMode: 'add' | 'edit' = 'add';
  selected: any[] = [];
  @ViewChild('rowModal') rowModal!: IonModal;

  sajatIntezmeny: any;

  constructor(
    private http: HttpClient,
    private data: DataService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
  ) {
    this.metaForm = this.fb.group({
      id: new FormControl(null),
      table_name: new FormControl('', [Validators.required, Validators.pattern(/^[a-zA-Z_][a-zA-Z0-9_]{0,63}$/)]),
      display_name: new FormControl('', [Validators.required]),
      intezmeny_field: new FormControl(''),
      id_field: new FormControl('id', [Validators.required]),
      ordsz: new FormControl(0),
      menu_kategoria: new FormControl(null),
    });
    this.rowForm = this.fb.group({});
  }

  async ngOnInit() {
    this.sajatIntezmeny = await this.data.getFromStorage('sajatIntezmeny');
    this.route.queryParams.subscribe(params => {
      this.activeMeta = null;
      this.columns = [];
      this.rows = [];
      this.selected = [];
      const tableName = params['table'];
      if (tableName) {
        // Nézet mód: csak az adat-grid látszódik, az adott tábla auto-kiválasztva
        this.tableMode = true;
        this.menuKategoria = null;
        this.http.get<{ columns: ColumnMeta[]; meta: any }>(`/api/torzs/columns/${encodeURIComponent(tableName)}`).subscribe({
          next: res => {
            this.activeMeta = res.meta;
            this.columns = res.columns;
            this.tablaMeta = [res.meta];
            this.loadRows();
          },
          error: err => this.data.sendToast('Hiba: ' + (err.error?.error ?? err.message), 'error'),
        });
      } else {
        // Admin mód: mindkét panel látszódik
        this.tableMode = false;
        this.menuKategoria = params['kategoria'] ?? null;
        this.loadTableList();
        this.loadDbTables();
      }
    });
  }

  // ─── Konfiguráció ────────────────────────────────────────────────────────

  loadTableList() {
    const url = this.menuKategoria
      ? `/api/torzs/meta?kategoria=${encodeURIComponent(this.menuKategoria)}`
      : '/api/torzs/meta';
    this.http.get<TorzsMeta[]>(url).subscribe({
      next: rows => this.tablaMeta = rows,
      error: err => this.data.sendToast('Hiba a tábla-lista betöltésekor: ' + (err.error?.error ?? err.message), 'error'),
    });
  }

  loadDbTables() {
    this.http.get<any[]>('/api/torzs/dbtables').subscribe({
      next: rows => this.dbTablas = rows,
      error: () => {},
    });
  }

  openAddMeta() {
    this.metaEditMode = 'add';
    this.metaForm.reset({ id: null, id_field: 'id', ordsz: 0, menu_kategoria: this.menuKategoria });
    this.metaModal.present();
  }

  openEditMeta(meta: TorzsMeta) {
    this.metaEditMode = 'edit';
    this.metaForm.setValue({
      id: meta.id,
      table_name: meta.table_name,
      display_name: meta.display_name,
      intezmeny_field: meta.intezmeny_field ?? '',
      id_field: meta.id_field,
      ordsz: meta.ordsz,
      menu_kategoria: meta.menu_kategoria ?? null,
    });
    this.metaModal.present();
  }

  saveMeta() {
    if (this.metaForm.invalid) return;
    const val = this.metaForm.value;
    if (!val.intezmeny_field) val.intezmeny_field = null;
    if (!val.menu_kategoria) val.menu_kategoria = null;
    this.http.post('/api/torzs/meta', val).subscribe({
      next: () => {
        this.metaModal.dismiss();
        this.loadTableList();
        this.data.sendToast('Mentve!', 'success');
      },
      error: err => this.data.sendToast('Hiba: ' + (err.error?.error ?? err.message), 'error'),
    });
  }

  deleteMeta(meta: TorzsMeta) {
    if (!confirm(`Eltávolítja a(z) "${meta.display_name}" táblát a törzsadat-listából?`)) return;
    this.http.delete(`/api/torzs/meta/${meta.id}`).subscribe({
      next: () => {
        if (this.activeMeta?.id === meta.id) {
          this.activeMeta = null;
          this.columns = [];
          this.rows = [];
        }
        this.loadTableList();
        this.data.sendToast('Törölve!', 'success');
      },
      error: err => this.data.sendToast('Hiba: ' + (err.error?.error ?? err.message), 'error'),
    });
  }

  // ─── Adatok ──────────────────────────────────────────────────────────────

  selectTable(meta: TorzsMeta) {
    this.activeMeta = meta;
    this.selected = [];
    this.rows = [];
    this.filteredRows = [];
    this.filterText = '';
    this.page = 1;
    this.loadColumns();
    this.loadRows();
  }

  loadColumns() {
    if (!this.activeMeta) return;
    this.http.get<{ columns: ColumnMeta[]; meta: TorzsMeta }>(`/api/torzs/columns/${this.activeMeta.table_name}`)
      .subscribe({
        next: res => {
          this.columns = res.columns;
          this.recalculateTable();
        },
        error: err => this.data.sendToast('Oszlopok betöltési hibája: ' + (err.error?.error ?? err.message), 'error'),
      });
  }

  loadRows() {
    if (!this.activeMeta) return;
    this.loading = true;
    const intezmeny = this.activeMeta.intezmeny_field ? this.sajatIntezmeny?.intezmenyId : undefined;
    const params: any = { page: this.page, limit: this.limit };
    if (intezmeny) params.intezmeny = intezmeny;

    this.http.get<{ total: number; rows: any[]; meta: TorzsMeta }>(`/api/torzs/rows/${this.activeMeta.table_name}`, { params })
      .subscribe({
        next: res => {
          this.rows = res.rows;
          this.filteredRows = res.rows;
          this.total = res.total;
          this.filterText = '';
          this.loading = false;
          this.recalculateTable();
        },
        error: err => {
          this.loading = false;
          this.data.sendToast('Adatok betöltési hibája: ' + (err.error?.error ?? err.message), 'error');
        },
      });
  }

  private recalculateTable() {
    setTimeout(() => {
      this.table?.recalculate();
      this.table?.recalculateColumns();
    });
  }

  // ─── Szerkesztő ──────────────────────────────────────────────────────────

  /** Dinamikus form összeállítása az oszlopok alapján */
  private buildRowForm(initialValues: any = {}) {
    const group: { [key: string]: FormControl } = {};
    for (const col of this.columns) {
      const raw = this.rowEditMode === 'add' && this.isUuidDefault(col) && !initialValues[col.Field]
        ? this.generateUuid()
        : initialValues[col.Field];
      const val = this.normalizeFieldValue(raw, col);
      const isRequired = col.Null === 'NO' && col.Extra !== 'auto_increment' && col.Key !== 'PRI';
      group[col.Field] = new FormControl(val, isRequired ? [Validators.required] : []);
    }
    this.rowForm = this.fb.group(group);
  }

  /** DB értéket az input típusnak megfelelő formára hozza, null-t kezelve */
  private normalizeFieldValue(raw: any, col: ColumnMeta): any {
    if (raw === null || raw === undefined) return '';
    const t = col.Type?.toLowerCase() ?? '';
    if (this.isDateLikeColumn(col)) {
      return this.toDateInputValue(raw);
    }
    return raw;
  }

  private isDateLikeColumn(col: ColumnMeta): boolean {
    const t = col.Type?.toLowerCase() ?? '';
    return t.includes('date') || t.includes('datetime') || t.includes('timestamp');
  }

  private isUuidDefault(col: ColumnMeta): boolean {
    const defaultValue = String(col.Default ?? '').trim().toLowerCase();
    return defaultValue === 'uuid()' || defaultValue === '(uuid())';
  }

  private generateUuid(): string {
    const cryptoRef = globalThis.crypto;
    if (cryptoRef?.randomUUID) return cryptoRef.randomUUID();

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, char => {
      const random = Math.random() * 16 | 0;
      const value = char === 'x' ? random : (random & 0x3 | 0x8);
      return value.toString(16);
    });
  }

  private toDateInputValue(raw: any): string {
    if (raw === null || raw === undefined || raw === '') return '';
    if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
      return raw.toISOString().slice(0, 10);
    }

    const value = String(raw).trim();
    const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) return match[1];

    const date = new Date(value.includes(' ') ? value.replace(' ', 'T') : value);
    return Number.isNaN(date.getTime()) ? value : date.toISOString().slice(0, 10);
  }

  private prepareRowForSave(row: any): any {
    const payload = { ...row };
    for (const col of this.columns) {
      if (this.isDateLikeColumn(col)) {
        payload[col.Field] = this.toDateInputValue(payload[col.Field]);
      }

      if (this.rowEditMode === 'add' && this.isUuidDefault(col) && !payload[col.Field]) {
        payload[col.Field] = this.generateUuid();
      }
    }
    return payload;
  }

  openAddRow() {
    if (!this.activeMeta) return;
    this.rowEditMode = 'add';
    // Ha az intézmény mező ismert, előre töltjük
    const defaults: any = {};
    if (this.activeMeta.intezmeny_field && this.sajatIntezmeny?.intezmenyId) {
      defaults[this.activeMeta.intezmeny_field] = this.sajatIntezmeny.intezmenyId;
    }
    this.buildRowForm(defaults);
    this.rowModal.present();
  }

  openEditRow() {
    if (!this.activeMeta || this.selected.length === 0) return;
    this.rowEditMode = 'edit';
    this.buildRowForm(this.selected[0]);
    this.rowModal.present();
  }

  saveRow() {
    if (this.rowForm.invalid || !this.activeMeta) return;
    const val = this.prepareRowForSave(this.rowForm.value);
    val.__mode = this.rowEditMode;
    const idColumn = this.columns.find(col => col.Field === this.activeMeta?.id_field);
    // INSERT esetén csak az auto increment id mezőt hagyjuk el. UUID-s kulcsot küldünk.
    if (this.rowEditMode === 'add' && idColumn?.Extra === 'auto_increment') {
      delete val[this.activeMeta.id_field];
    }
    this.http.post(`/api/torzs/rows/${this.activeMeta.table_name}`, val).subscribe({
      next: () => {
        this.refreshCachedFeladatellatasSettings(val);
        this.rowModal.dismiss();
        this.loadRows();
        this.selected = [];
        this.data.sendToast('Mentve!', 'success');
      },
      error: err => this.data.sendToast('Hiba: ' + (err.error?.error ?? err.message), 'error'),
    });
  }

  private async refreshCachedFeladatellatasSettings(row: any) {
    if (this.activeMeta?.table_name !== 'feladatellatas') return;
    if (!this.sajatIntezmeny?.intezmenyId || row?.id !== this.sajatIntezmeny.intezmenyId) return;

    const updated = {
      ...this.sajatIntezmeny,
      ertekelesAtlagTizedes: row.ertekeles_atlag_tizedes ?? this.sajatIntezmeny.ertekelesAtlagTizedes,
      ertekeles_atlag_tizedes: row.ertekeles_atlag_tizedes ?? this.sajatIntezmeny.ertekeles_atlag_tizedes,
    };

    this.sajatIntezmeny = updated;
    await this.data.setStorage('sajatIntezmeny', updated);
  }

  deleteRow() {
    if (!this.activeMeta || this.selected.length === 0) return;
    if (!confirm(`Biztosan törli a kijelölt ${this.selected.length} sort?`)) return;
    const idField = this.activeMeta.id_field;
    const deletes = this.selected.map(row =>
      this.http.delete(`/api/torzs/rows/${this.activeMeta!.table_name}/${row[idField]}`).toPromise()
    );
    Promise.all(deletes).then(() => {
      this.loadRows();
      this.selected = [];
      this.data.sendToast('Törölve!', 'success');
    }).catch(err => this.data.sendToast('Hiba: ' + (err.error?.error ?? err.message), 'error'));
  }

  updateFilter(val: string) {
    this.filterText = val;
    const term = val.toLowerCase().trim();
    if (!term) {
      this.filteredRows = [...this.rows];
    } else {
      this.filteredRows = this.rows.filter(row =>
        Object.values(row).some(v => String(v ?? '').toLowerCase().includes(term))
      );
    }
    if (this.table) this.table.offset = 0;
  }

  onLimitChange(event: Event) {
    const limit = (event.target as HTMLSelectElement).value;
    this.currentPageLimit = parseInt(limit, 10);
    if (this.table) {
      this.table.limit = this.currentPageLimit;
      this.table.recalculate();
      setTimeout(() => {
        if (this.table.bodyComponent.temp.length <= 0) {
          this.table.offset = Math.floor((this.table.rowCount - 1) / this.table.limit);
        }
      });
    }
  }

  onSelect(event: any) {
    this.selected = event.selected;
  }

  /** Megmondja, hogy egy oszlop egy id/fk mező-e (nagyobb szélességet kap) */
  colWidth(col: ColumnMeta): number {
    if (col.Type?.includes('varchar') || col.Type?.includes('text')) return 200;
    if (col.Type?.includes('int')) return 100;
    return 140;
  }

  columnLabel(col: ColumnMeta): string {
    return (col.Comment || '').trim() || col.Field;
  }

  /** Input type HTML elemhez */
  inputType(col: ColumnMeta): string {
    const t = col.Type?.toLowerCase() ?? '';
    if (t.includes('int') || t.includes('decimal') || t.includes('float') || t.includes('double')) return 'number';
    if (this.isDateLikeColumn(col)) return 'date';
    return 'text';
  }

  /** Soronkénti értéket olvasható formában ad vissza (hosszú szöveg rövidítve) */
  displayValue(val: any): string {
    if (val === null || val === undefined) return '';
    const s = String(val);
    return s.length > 60 ? s.substring(0, 57) + '…' : s;
  }
}
